import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BuddyInvitesProvider } from "./context/BuddyInvitesContext";
import { ChallengesProvider } from "./context/ChallengesContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import api from "./lib/api";
import { setAuthToken } from "./lib/api";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Library from "./pages/Library";
import WorkoutBuilder from "./pages/WorkoutBuilder";
import ExerciseDetail from "./pages/ExerciseDetail";
import CalendarPage from "./pages/CalendarPage";
import ActiveWorkout from "./pages/ActiveWorkout";
import Progress from "./pages/Progress";
import Social from "./pages/Social";
import Profile from "./pages/Profile";
import Landing from "./pages/Landing";
import Coach from "./pages/Coach";
import SharedProgress from "./pages/SharedProgress";
import Pricing from "./pages/Pricing";
import PaymentSuccess from "./pages/PaymentSuccess";
import Cardio from "./pages/Cardio";
import Admin from "./pages/Admin";
import SupersetImport from "./pages/SupersetImport";
import WorkoutImport from "./pages/WorkoutImport";
import InstallHelp from "./pages/InstallHelp";
import ScholarshipClaim from "./pages/ScholarshipClaim";
import TrainerHub from "./pages/TrainerHub";
import TrainerDirectory from "./pages/TrainerDirectory";
import TrainerInviteLanding from "./pages/TrainerInviteLanding";
import WorkoutHistory from "./pages/WorkoutHistory";
import BottomNav from "./components/BottomNav";
import OfflineIndicator from "./components/OfflineIndicator";
import AppUpdateBanner from "./components/AppUpdateBanner";
import ForceRefreshButton from "./components/ForceRefreshButton";
import ActiveWorkoutBanner from "./components/ActiveWorkoutBanner";
import ChallengeWinCelebration from "./components/ChallengeWinCelebration";
import GoalHitCelebration from "./components/GoalHitCelebration";
import StreakMilestoneCelebration from "./components/StreakMilestoneCelebration";

function AuthCallback() {
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { navigate("/login"); return; }
    const session_id = m[1];
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id });
        if (data?.token) setAuthToken(data.token);
        window.history.replaceState({}, "", "/dashboard");
        window.location.href = "/dashboard";
      } catch (e) {
        toast.error("Google sign-in failed");
        navigate("/login");
      }
    })();
  }, [navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white">
      <div className="font-display text-3xl">Signing you in…</div>
    </div>
  );
}

function ProtectedLayout({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white/70 font-display text-xl">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen bg-neutral-950 text-white" style={{ paddingBottom: "calc(5rem + env(safe-area-inset-bottom))" }}>
      <ForceRefreshButton />
      {children}
      <ActiveWorkoutBanner />
      <BottomNav />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/share/:token" element={<SharedProgress />} />
      <Route path="/pricing" element={<ProtectedLayout><Pricing /></ProtectedLayout>} />
      <Route path="/payments/success" element={<ProtectedLayout><PaymentSuccess /></ProtectedLayout>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={<ProtectedLayout><Dashboard /></ProtectedLayout>} />
      <Route path="/library" element={<ProtectedLayout><Library /></ProtectedLayout>} />
      <Route path="/workouts/new" element={<ProtectedLayout><WorkoutBuilder /></ProtectedLayout>} />
      <Route path="/workouts/:id" element={<ProtectedLayout><WorkoutBuilder /></ProtectedLayout>} />
      <Route path="/supersets/import/:token" element={<ProtectedLayout><SupersetImport /></ProtectedLayout>} />
      <Route path="/workouts/import/:token" element={<ProtectedLayout><WorkoutImport /></ProtectedLayout>} />
      <Route path="/install" element={<InstallHelp />} />
      <Route path="/claim/:token" element={<ScholarshipClaim />} />
      <Route path="/exercises/:id" element={<ProtectedLayout><ExerciseDetail /></ProtectedLayout>} />
      <Route path="/calendar" element={<ProtectedLayout><CalendarPage /></ProtectedLayout>} />
      <Route path="/active/:workoutId" element={<ProtectedLayout><ActiveWorkout /></ProtectedLayout>} />
      <Route path="/workouts/:workoutId/history" element={<ProtectedLayout><WorkoutHistory /></ProtectedLayout>} />
      <Route path="/progress" element={<ProtectedLayout><Progress /></ProtectedLayout>} />
      <Route path="/social" element={<ProtectedLayout><Social /></ProtectedLayout>} />
      <Route path="/coach" element={<ProtectedLayout><Coach /></ProtectedLayout>} />
      <Route path="/cardio" element={<ProtectedLayout><Cardio /></ProtectedLayout>} />
      <Route path="/admin" element={<ProtectedLayout><Admin /></ProtectedLayout>} />
      <Route path="/profile" element={<ProtectedLayout><Profile /></ProtectedLayout>} />
      <Route path="/trainer" element={<ProtectedLayout><TrainerHub /></ProtectedLayout>} />
      <Route path="/trainers" element={<ProtectedLayout><TrainerDirectory /></ProtectedLayout>} />
      <Route path="/trainer-invite/:token" element={<TrainerInviteLanding />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <WebSocketProvider>
        <BuddyInvitesProvider>
          <ChallengesProvider>
            <BrowserRouter>
              <OfflineIndicator />
              <AppUpdateBanner />
              <ChallengeWinCelebration />
              <GoalHitCelebration />
              <StreakMilestoneCelebration />
              <AppRouter />
              <Toaster theme="dark" position="top-center" />
            </BrowserRouter>
          </ChallengesProvider>
        </BuddyInvitesProvider>
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;
